/**
 * @reflecto/core — filetransfer/p2p.ts
 *
 * P2P (M3) normative constants (docs/file-transfer-p2p.md §15). This is the single TS home
 * for the M3 constants table — the Kotlin mirror is `P2pConstants` in
 * `apps/android/.../filetransfer/P2pProtocol.kt`. Both sides are a `type-sync-check` parity
 * surface; keep values byte/number-identical.
 *
 * TASK-22 declared the signaling-only constants below. TASK-23 (extension transport) adds the
 * connect-flow timers, the DataChannel framing/backpressure constants, the sentinel prefixes,
 * and the STUN/protocol config it consumes (§16 pt.2). TASK-25 (this file's remaining rows) adds
 * the `TRANSFERRING_P2P`/`P2P_AWAIT_VERIFY`-state controller timers (`FT_P2P_STALL_MS`,
 * `FT_P2P_COMPLETE_WAIT_MS`) and the §3 kill switch (`FT_P2P_ENABLED`) — this file stays the
 * designated single home per §15, not a new one per task.
 */

/** Parity marker only — never travels on the wire (§0). Bump alongside any starred §15 constant. */
export const FT_P2P_SPEC_VERSION = 1;

/**
 * §3 eligibility gate / compile-time kill switch — `true` in M3 builds. TASK-25's tests toggle
 * this (via module mocking) to assert the pure-M2-relay fallback when P2P is disabled.
 */
export const FT_P2P_ENABLED = true;

/**
 * FCM 4096-byte data-payload ceiling, expressed as a serialized-plaintext-envelope budget
 * (§4.4's normative derivation: 61 fixed FCM key/value bytes + base64(0x01 | nonce | ciphertext)
 * of the envelope ⇒ P ≤ 2983, pinned here with an 83-byte safety margin). Enforced on every
 * `p2p_signal` send regardless of target device type (§4.4).
 */
export const FT_P2P_MAX_SIGNAL_PLAINTEXT_BYTES = 2900;

/** Cap on candidates buffered before the remote SDP is applied (§4.5). Reserved-path defense — no candidate signal is ever emitted in M3 (§4.2). */
export const FT_P2P_MAX_BUFFERED_CANDIDATES = 32;

/**
 * Full-ICE gather-or-timeout bound (§4.2) — both roles wait for
 * `iceGatheringState === "complete"` OR this many ms, whichever is first, before sending their
 * SDP. Consumed by TASK-23's `p2pTransport.ts` `connect()`.
 */
export const FT_P2P_GATHER_TIMEOUT_MS = 3_000;

/**
 * Sender's `CONNECTING_P2P` open-timeout (§5.5) — armed when the offer ctrl is handed off,
 * RESTARTED (once) to a fresh window when the answer is applied. Also the receiver's
 * post-offer open timer (§5.5's "swap window -> 10 s" row) — one value shared by both roles'
 * post-SDP wait per §5.5's table.
 */
export const FT_P2P_OPEN_TIMEOUT_MS = 10_000;

/**
 * Receiver's pre-offer `CONNECTING_P2P` window (§5.5) — armed when the accept ctrl is handed
 * off; replaced by a fresh `FT_P2P_OPEN_TIMEOUT_MS` timer the instant the offer is applied.
 */
export const FT_P2P_RECEIVER_WINDOW_MS = 20_000;

/**
 * ACK cadence (§5.3 step 6) — the receiver acks every this-many verified chunks (also always on
 * the final chunk, `c === chunkCount`, regardless of cadence alignment).
 */
export const FT_P2P_ACK_EVERY_CHUNKS = 64;

/**
 * Sender + receiver `TRANSFERRING_P2P` stall detector (§5.5) — sender: reset on every ACK that
 * raises `lastAckedCount`, armed only while `chunksSent > lastAckedCount`; receiver: reset on
 * every completed chunk, armed while `chunksVerified(mem) < chunkCount`. Fires -> demotion (§8.2/§8.3).
 */
export const FT_P2P_STALL_MS = 30_000;

/**
 * Sender's `P2P_AWAIT_VERIFY` complete-wait timer (§5.5/§9.2) — started on the final ACK
 * (`ackedCount === chunkCount`); fires -> §8.3 demotion with `lvc = chunkCount` (empty uploadSet,
 * straight to `/complete`).
 */
export const FT_P2P_COMPLETE_WAIT_MS = 60_000;

/**
 * CONFIRMED (§0 freeze rule) — DataChannel message split size (§5.2): a chunk frame
 * (8 B BE index ‖ ciphertext) is split into consecutive DC messages of exactly this many bytes,
 * last message shorter. TASK-23's manual loopback harness (`apps/extension/test/manual/
 * p2p-loopback.html`) was the designated live-verification point, but was never actually run for
 * a real measurement (its own header comment: "pending that run"). TASK-26's automated
 * Puppeteer loopback (`apps/extension/test/integration/filetransfer-p2p.test.ts`, driving the
 * REAL `connect()`/`sendChunkFrame` path against real headless Chrome, not a standalone
 * reimplementation) supplied the first real measurement: every 16,384-byte message sent by the
 * real production send loop was accepted with zero `send()` errors. Per §0, a confirmed default
 * freezes as-is — this value does not change.
 */
export const FT_P2P_FRAME_MAX_BYTES = 16_384;

/**
 * CONFIRMED (§0 freeze rule) — `dc.bufferedAmountLowThreshold` / send-gate threshold (§5.4).
 * Same verification status and source as `FT_P2P_FRAME_MAX_BYTES` above: TASK-26's real-Chrome
 * loopback observed `bufferedAmount` exceeding this threshold and `bufferedamountlow` firing
 * (backpressure engaging and resuming sends) multiple times over a ~20 MB transfer, with the
 * transfer still completing byte-identical — confirming the threshold functions as specified.
 * This value does not change.
 */
export const FT_P2P_BUFFERED_LOW_BYTES = 1_048_576;

/**
 * ACK/COMPLETE frame sentinel prefixes (§5.2/§15) — frozen wire values. Declared as 8-byte
 * `Uint8Array`s compared byte-wise (§15's pinned representation: 2⁶⁴−1/2⁶⁴−2 exceed
 * `Number.MAX_SAFE_INTEGER`, so these are never decoded as JS numbers, only matched as exact
 * byte prefixes). NOT `Object.freeze`d — V8 cannot freeze a non-empty `TypedArray` (its indexed
 * properties are always configurable: `Object.freeze` throws "Cannot freeze array buffer views
 * with elements"). Callers must treat these as shared singletons and never mutate in place.
 */
export const FT_P2P_ACK_SENTINEL: Uint8Array = new Uint8Array([
  0xff, 0xff, 0xff, 0xff, 0xff, 0xff, 0xff, 0xff,
]);
export const FT_P2P_COMPLETE_SENTINEL: Uint8Array = new Uint8Array([
  0xff, 0xff, 0xff, 0xff, 0xff, 0xff, 0xff, 0xfe,
]);

/** STUN-only ICE config (§5.1) — no TURN, ever. */
export const FT_P2P_STUN_URLS: readonly string[] = [
  "stun:stun.l.google.com:19302",
  "stun:stun1.l.google.com:19302",
];

/** DataChannel `protocol` field (§5.1) — frozen wire value. */
export const FT_P2P_DC_PROTOCOL = "reflecto-ft-v1";
